import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Mail, Download, Play } from "lucide-react";
import { MaskedLine, FadeUp, Page, Meta } from "../components/Reveal";
import { useLinks } from "../lib/LinksContext";
import { useContent } from "../lib/ContentContext";
import { SOCIAL_LINKS, TikTokIcon } from "../lib/socials";

const LOGOS = [
  { key: "lapresse", name: "La Presse", src: "/logos/lapresse.png", url: "https://www.lapresse.ca/societe/sante/2025-02-02/vaginisme/quand-faire-l-amour-c-est-l-enfer.php" },
  { key: "tva", name: "TVA Nouvelles", src: "/logos/tva.png", url: "https://www.tvanouvelles.ca/2021/02/14/saint-valentin-certains-ont-hate-que-la-journee-soit-passee" },
  { key: "rouge", name: "102,7 Rouge", src: "/logos/rouge.png", url: "" },
  { key: "petitbambou", name: "Petit BamBou", src: "/logos/petitbambou.webp", url: "https://www.petitbambou.com/fr/contributor/eugenie-larrivee" },
  { key: "tv5", name: "TV5+", src: "/logos/tv5.png", url: "https://www.tv5plus.ca/videos/sexe-techno/saisons/2/episodes/2" },
  { key: "latribune", name: "La Tribune", src: "/logos/latribune.png", url: "https://www.latribune.ca/2021/02/13/et-si-ce-netait-pas-un-conte-de-fees-2b7a1ec06c670efc0ca64f18aa7a5250/" },
  { key: "lesradieuses", name: "Les Radieuses", src: "/logos/lesradieuses.svg", url: "https://lesradieuses.com/ralentir-pour-mieux-ressentir-le-slow-sex/" },
];

const APPEARANCES = [
  { cat: "Télévision", media: "TVA Nouvelles", title: "Saint-Valentin : certains ont hâte que la journée soit passée", date: "Février 2021", url: "https://www.tvanouvelles.ca/2021/02/14/saint-valentin-certains-ont-hate-que-la-journee-soit-passee" },
  { cat: "Radio", media: "102,7 Rouge", title: "60 chroniques en direct du studio dans La Gang du Matin : la sexualité sans détour, chaque semaine pendant 4 saisons", date: "2022 à 2024", url: "#chroniques" },
  { cat: "Presse", media: "La Presse", title: "Vaginisme : quand faire l'amour, c'est l'enfer", date: "Février 2025", url: "https://www.lapresse.ca/societe/sante/2025-02-02/vaginisme/quand-faire-l-amour-c-est-l-enfer.php" },
  { cat: "Presse", media: "La Presse", title: "Petit guide de santé sexuelle en temps de confinement", date: "Avril 2020", url: "https://www.lapresse.ca/societe/sexualite/2020-04-27/petit-guide-de-sante-sexuelle-en-temps-de-confinement" },
  { cat: "Presse", media: "La Tribune", title: "Et si ce n'était pas un conte de fées ?", date: "Février 2021", url: "https://www.latribune.ca/2021/02/13/et-si-ce-netait-pas-un-conte-de-fees-2b7a1ec06c670efc0ca64f18aa7a5250/" },
  { cat: "Presse", media: "Les Radieuses", title: "Ralentir pour mieux ressentir : le slow sex", date: "2024", url: "https://lesradieuses.com/ralentir-pour-mieux-ressentir-le-slow-sex/" },
  { cat: "Plateformes", media: "Petit BamBou", title: "Contributrice : un programme de méditation dédié à la sexualité et à la reconnexion au corps", date: "En ligne", url: "https://www.petitbambou.com/fr/contributor/eugenie-larrivee" },
  { cat: "Plateformes", media: "TV5+", title: "Sexe-Techno : saison 2, épisode 2", date: "", url: "https://www.tv5plus.ca/videos/sexe-techno/saisons/2/episodes/2" },
];

const CATEGORIES = ["Télévision", "Radio", "Presse", "Plateformes"];

// Ajouter ici les capsules vidéo des chroniques (liens YouTube) dès qu'elles sont disponibles.
const RADIO_VIDEOS = [];

const PRESS_PHOTOS = [
  { src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_eugenie-media/artifacts/5h0vu5b1_SEXU-317.webp", label: "Portrait : grand sourire" },
  { src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_eugenie-media/artifacts/jhc171gj_SEXU-325%20copie%202.webp", label: "Portrait : posé, fond gris" },
  { src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_eugenie-media/artifacts/3dqkzsok_SEXU-3-1.webp", label: "Portrait : regard caméra" },
];

function youtubeEmbed(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function Medias() {
  const links = useLinks();
  const c = useContent();
  const instagram = SOCIAL_LINKS.find((s) => s.key === "instagram");
  const SUJETS = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => c(`medias.presse.sujet.${n}`));

  return (
    <Page testId="medias-page">
      <section className="mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48">
        <FadeUp><Meta>{c("medias.hero.meta")}</Meta></FadeUp>
        <h1 className="mt-6 font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-7xl">
          <MaskedLine delay={0.2}>{c("medias.hero.l1")}</MaskedLine>
          <MaskedLine delay={0.35} className="italic text-clay">{c("medias.hero.l2")}</MaskedLine>
        </h1>
      </section>

      {/* LOGOS */}
      <section data-testid="logos-section" className="mx-auto max-w-7xl px-6 pt-20 md:px-12 md:pt-28">
        <FadeUp><Meta>{c("medias.logos.meta")}</Meta></FadeUp>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {LOGOS.map((l, i) => {
            const inner = (
              <>
                <div className="flex h-16 items-center justify-center">
                  <img src={l.src} alt={`Logo ${l.name}`}
                    className="max-h-14 max-w-[70%] object-contain opacity-75 transition-all duration-500 group-hover:opacity-100" />
                </div>
                <p className="mt-4 flex items-center justify-center gap-1.5 text-xs font-medium uppercase tracking-[0.15em] text-ash transition-colors duration-300 group-hover:text-clay">
                  {l.name}
                  {l.url && <ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />}
                </p>
              </>
            );
            const cls = "group rounded-[1.75rem] border border-linen bg-shell p-6 text-center transition-colors duration-500 hover:bg-sand/30";
            return (
              <FadeUp key={l.key} delay={0.05 * i}>
                {l.url ? (
                  <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`media-logo-${l.key}`} className={`block ${cls}`}>{inner}</a>
                ) : (
                  <div data-testid={`media-logo-${l.key}`} className={cls}>{inner}</div>
                )}
              </FadeUp>
            );
          })}
          <FadeUp delay={0.35}>
            <div className="flex h-full flex-col items-center justify-center rounded-[1.75rem] border border-dashed border-linen p-6 text-center">
              <p className="font-serif text-lg font-light italic leading-snug text-smoke">
                {c("medias.logos.fin")}
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* APPARITIONS */}
      <section data-testid="appearances-section" className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        {CATEGORIES.map((cat, ci) => (
          <div key={cat} className={ci > 0 ? "mt-16" : ""}>
            <FadeUp>
              <h2 className="font-serif text-2xl font-light italic text-clay md:text-3xl">{cat}</h2>
            </FadeUp>
            <div className="mt-6">
              {APPEARANCES.filter((a) => a.cat === cat).map((a, i) => {
                const rowCls = "group grid items-baseline gap-2 border-b border-linen py-6 transition-colors duration-300 hover:bg-shell/70 md:grid-cols-12 md:gap-6 md:px-4";
                const rowId = `appearance-${cat.toLowerCase().replace(/[^a-z]/g, "-")}-${i + 1}`;
                const cells = (
                  <>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-ash md:col-span-2">{a.media}</p>
                    <p className="font-serif text-xl font-light leading-snug tracking-tight transition-colors duration-300 group-hover:text-clay md:col-span-8 md:text-2xl">
                      {a.title}
                    </p>
                    <p className="flex items-center gap-2 text-xs text-ash md:col-span-2 md:justify-end">
                      {a.date}
                      {a.url && <ArrowUpRight className="h-4 w-4 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:text-clay" />}
                    </p>
                  </>
                );
                return (
                  <FadeUp key={a.title} delay={0.06 * i}>
                    {a.url && !a.url.startsWith("#") ? (
                      <a href={a.url} target="_blank" rel="noopener noreferrer" data-testid={rowId} className={rowCls}>{cells}</a>
                    ) : a.url ? (
                      <a href={a.url} data-testid={rowId} className={rowCls}>{cells}</a>
                    ) : (
                      <div data-testid={rowId} className={rowCls}>{cells}</div>
                    )}
                  </FadeUp>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* CHRONIQUES RADIO — VIDÉOS */}
      <section id="chroniques" data-testid="chroniques-section" className="scroll-mt-32 bg-shell py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <FadeUp><Meta>{c("medias.chroniques.meta")}</Meta></FadeUp>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
                {c("medias.chroniques.t1")} <em className="text-clay">{c("medias.chroniques.t2")}</em>
              </h2>
              <FadeUp delay={0.15}>
                <p className="mt-8 max-w-md text-base leading-relaxed text-smoke md:text-lg">
                  {c("medias.chroniques.p")}
                </p>
                <div className="mt-8 flex items-center gap-4 rounded-[1.75rem] border border-linen bg-cream p-5">
                  <img src="/logos/rouge.png" alt="Logo 102,7 Rouge" className="h-8 w-auto object-contain" />
                  <p className="text-xs leading-relaxed text-ash">
                    {c("medias.chroniques.legende")}
                  </p>
                </div>
              </FadeUp>
            </div>
            <div className="lg:col-span-7">
              {RADIO_VIDEOS.length > 0 ? (
                <div className="grid gap-6 sm:grid-cols-2">
                  {RADIO_VIDEOS.map((v, i) => (
                    <FadeUp key={v.url} delay={0.08 * i}>
                      <div data-testid={`chronique-video-${i + 1}`} className="overflow-hidden rounded-[1.75rem] border border-linen bg-cream">
                        <div className="aspect-video">
                          <iframe src={youtubeEmbed(v.url)} title={v.title} loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen className="h-full w-full" />
                        </div>
                        <p className="p-5 text-sm text-smoke">{v.title}</p>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              ) : (
                <FadeUp delay={0.2}>
                  <div data-testid="chroniques-placeholder"
                    className="flex aspect-video flex-col items-center justify-center rounded-[1.75rem] border border-dashed border-clay/30 bg-cream/60 p-10 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-clay/10">
                      <Play className="h-6 w-6 text-clay" strokeWidth={1.5} />
                    </span>
                    <p className="mt-6 font-serif text-2xl font-light italic text-charcoal">
                      {c("medias.chroniques.placeholder.titre")}
                    </p>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-smoke">
                      {c("medias.chroniques.placeholder.texte")}
                    </p>
                  </div>
                </FadeUp>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* RÉSEAUX SOCIAUX */}
      <section data-testid="socials-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <FadeUp><Meta>{c("medias.social.meta")}</Meta></FadeUp>
        <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
          {c("medias.social.t1")} <em className="text-clay">{c("medias.social.t2")}</em>
        </h2>
        <FadeUp delay={0.1}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
            {c("medias.social.p")}
          </p>
        </FadeUp>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {instagram && (
            <FadeUp delay={0.1}>
              {links.instagram ? (
                <a href={links.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-card-instagram"
                  className="group flex items-center justify-between gap-6 rounded-[1.75rem] border border-linen bg-shell p-8 transition-colors duration-500 hover:bg-sand/30">
                  <div className="flex items-center gap-5">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-clay/10 transition-colors duration-300 group-hover:bg-clay/20">
                      <instagram.Icon className="h-6 w-6 text-clay" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-serif text-2xl font-light tracking-tight">Instagram</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ash">{c("medias.social.handle")}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 group-hover:bg-charcoal">
                    {c("medias.social.suivre")}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              ) : (
                <div data-testid="social-card-instagram"
                  className="flex items-center gap-5 rounded-[1.75rem] border border-dashed border-linen bg-shell/60 p-8">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-clay/10">
                    <instagram.Icon className="h-6 w-6 text-clay" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="font-serif text-2xl font-light tracking-tight">Instagram</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ash">{c("medias.social.bientot")}</p>
                  </div>
                </div>
              )}
            </FadeUp>
          )}
          <FadeUp delay={0.15}>
            {links.tiktok ? (
              <a href={links.tiktok} target="_blank" rel="noopener noreferrer" data-testid="social-card-tiktok"
                className="group flex items-center justify-between gap-6 rounded-[1.75rem] border border-linen bg-shell p-8 transition-colors duration-500 hover:bg-sand/30">
                <div className="flex items-center gap-5">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-moss/10 transition-colors duration-300 group-hover:bg-moss/20">
                    <TikTokIcon className="h-6 w-6 text-moss" />
                  </span>
                  <div>
                    <p className="font-serif text-2xl font-light tracking-tight">TikTok</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ash">{c("medias.social.handle")}</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 group-hover:bg-charcoal">
                  {c("medias.social.suivre")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </a>
            ) : (
              <div data-testid="social-card-tiktok"
                className="flex items-center gap-5 rounded-[1.75rem] border border-dashed border-linen bg-shell/60 p-8">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-moss/10">
                  <TikTokIcon className="h-6 w-6 text-moss" />
                </span>
                <div>
                  <p className="font-serif text-2xl font-light tracking-tight">TikTok</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ash">{c("medias.social.bientot")}</p>
                </div>
              </div>
            )}
          </FadeUp>
        </div>
      </section>

      {/* ESPACE PRESSE */}
      <section data-testid="journaliste-section" className="bg-shell py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <FadeUp><Meta>{c("medias.presse.meta")}</Meta></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
              {c("medias.presse.t1")} <em className="text-clay">{c("medias.presse.t2")}</em>
            </h2>
          </FadeUp>

          <div className="mt-16 grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FadeUp>
                <h3 className="text-xs font-medium uppercase tracking-[0.25em] text-ash">{c("medias.presse.sujets.titre")}</h3>
                <div className="mt-5 flex flex-wrap gap-3">
                  {SUJETS.map((s, i) => (
                    <span key={s} data-testid={`sujet-${i + 1}`}
                      className="rounded-full border border-charcoal/15 px-5 py-2 text-xs font-medium uppercase tracking-[0.12em] text-smoke">
                      {s}
                    </span>
                  ))}
                </div>
              </FadeUp>

              <FadeUp delay={0.1}>
                <h3 className="mt-12 text-xs font-medium uppercase tracking-[0.25em] text-ash">{c("medias.presse.bioc.titre")}</h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-smoke md:text-base">
                  {c("medias.presse.bioc.texte")}
                </p>
              </FadeUp>

              <FadeUp delay={0.15}>
                <h3 className="mt-12 text-xs font-medium uppercase tracking-[0.25em] text-ash">{c("medias.presse.biol.titre")}</h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-smoke md:text-base">
                  {c("medias.presse.biol.texte")}
                </p>
              </FadeUp>
            </div>

            <div className="lg:col-span-5">
              <FadeUp>
                <h3 className="text-xs font-medium uppercase tracking-[0.25em] text-ash">{c("medias.presse.photos.titre")}</h3>
                <div className="mt-5 grid grid-cols-2 gap-4">
                  {PRESS_PHOTOS.map((p, i) => (
                    <div key={p.label} data-testid={`press-photo-${i + 1}`} className={i === 0 ? "col-span-2" : ""}>
                      <div className="overflow-hidden rounded-[1.75rem]">
                        <img src={p.src} alt={p.label} className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
                      </div>
                      <p className="mt-2 text-xs text-ash">{p.label}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs italic text-ash">{c("medias.presse.photos.note")}</p>
              </FadeUp>

              <FadeUp delay={0.1}>
                <div className="mt-10 rounded-[1.75rem] bg-cream p-8">
                  <h3 className="text-xs font-medium uppercase tracking-[0.25em] text-ash">{c("medias.presse.coord.titre")}</h3>
                  <p className="mt-4 flex items-center gap-3 text-sm text-smoke">
                    <Mail className="h-4 w-4 text-clay" />
                    <span data-testid="media-email">contact@eugenielarrivee.com</span>
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-ash">
                    {c("medias.presse.coord.note")}
                  </p>
                  <a href="/dossier-de-presse-eugenie-larrivee.pdf" download data-testid="press-kit-download"
                    className="group mt-6 inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                    <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                    {c("medias.presse.pdf")}
                  </a>
                </div>
              </FadeUp>
            </div>
          </div>

          <FadeUp delay={0.2}>
            <Link to="/contact" data-testid="medias-cta-contact"
              className="group mt-16 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
              {c("medias.presse.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </Page>
  );
}
